/**
 * Free gift with first-time subscription — Shopify theme script
 * Supports multiple gifts (all added together), and lets the customer remove
 * any gift without it coming straight back.
 *
 * Products can be excluded from triggering the gifts. Cart JSON has no tags,
 * so Liquid passes a list of excluded variant IDs (built from an automated
 * collection driven by a product tag) via window.freeGiftConfig.excludedVariantIds.
 * A subscription to an excluded product does not qualify the cart for gifts.
 *
 * Fast path: when the theme POSTs a subscription item to /cart/add.js, the gifts
 * are injected into that same request, so they arrive in one round trip and the
 * theme's own section render already contains them.
 *
 * Slow path: any other cart mutation triggers a reconcile that adds missing
 * gifts or removes orphaned ones — except gifts the customer removed on purpose.
 *
 * The cart count is patched in place rather than by re-rendering the
 * cart-icon-bubble section, because this theme's header markup and its
 * cart-icon-bubble section don't match (re-rendering nests a second icon).
 *
 * Install: assets/free-gift-subscription.js
 *   <script src="{{ 'free-gift-subscription.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  var cfg = window.freeGiftConfig || {};

  // ============================ EDIT THESE ============================
  var GIFT_VARIANT_IDS = [
    52741818483029,   // gift 1
    52743639794005,   // gift 2
    52741945655637    // gift 3
  ];

  var SECTIONS = [
    'cart-drawer',
    'main-cart-items',
    'main-cart-footer'
  ];

  var SELECTORS = {
    'cart-drawer': '#CartDrawer'
  };

  // Cart count is updated by text, not by section swap. Adjust if your header
  // uses different markup.
  var COUNT_BUBBLE = '.cart-count-bubble';
  var COUNT_LINK = '#cart-icon-bubble';

  var ELIGIBLE = cfg.eligible !== false;
  // ====================================================================

  var GIFT_FLAG = '_free_gift';
  var DECLINED_KEY = 'freeGiftDeclined';

  if (cfg.giftVariantIds) GIFT_VARIANT_IDS = cfg.giftVariantIds;
  if (cfg.sections) SECTIONS = cfg.sections;

  GIFT_VARIANT_IDS = GIFT_VARIANT_IDS.map(Number).filter(function (id) { return id > 0; });
  if (!GIFT_VARIANT_IDS.length) return;

  // Variants whose subscriptions must NOT trigger the free gifts.
  var EXCLUDED_VARIANT_IDS = (cfg.excludedVariantIds || [])
    .map(Number)
    .filter(function (id) { return id > 0; });

  function isExcluded(variantId) {
    return EXCLUDED_VARIANT_IDS.indexOf(Number(variantId)) !== -1;
  }

  var busy = false;
  var queued = false;
  var allGiftsPresent = false;   // cached so the fast path needs no lookup

  // Gifts seen in the cart on the previous reconcile. A gift that was present
  // and is now gone was removed by the customer, not simply never added.
  var lastSeen = null;

  // --- declined gifts (persist across page loads) ----------------------------

  function readDeclined() {
    try {
      var raw = sessionStorage.getItem(DECLINED_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  }

  function writeDeclined(ids) {
    try { sessionStorage.setItem(DECLINED_KEY, JSON.stringify(ids)); } catch (e) {}
  }

  function decline(id) {
    var d = readDeclined();
    if (d.indexOf(id) === -1) { d.push(id); writeDeclined(d); }
  }

  function isDeclined(id) {
    return readDeclined().indexOf(id) !== -1;
  }

  function clearDeclined() {
    try { sessionStorage.removeItem(DECLINED_KEY); } catch (e) {}
  }

  // --- helpers ---------------------------------------------------------------

  function json(url, body) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) { return r.json(); });
  }

  // A qualifying subscription: has a selling plan AND is not an excluded product.
  function isSubscription(item) {
    return !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) &&
           !isExcluded(item.variant_id);
  }

  function isGiftLine(item) {
    return GIFT_VARIANT_IDS.indexOf(item.variant_id) !== -1 &&
           !!(item.properties && item.properties[GIFT_FLAG]);
  }

  function giftItem(id) {
    var props = {};
    props[GIFT_FLAG] = 'true';
    return { id: id, quantity: 1, properties: props };
  }

  function addGifts(ids) {
    return json('/cart/add.js', {
      items: ids.map(giftItem),
      sections: SECTIONS,
      sections_url: window.location.pathname
    });
  }

  // One request for any number of line changes. Keyed by line item key, so it
  // never touches a non-gift line that happens to share a variant.
  function updateLines(updates) {
    return json('/cart/update.js', {
      updates: updates,
      sections: SECTIONS,
      sections_url: window.location.pathname
    });
  }

  // Patch the number without replacing the icon markup.
  function updateCartCount(count) {
    if (typeof count !== 'number') return;

    var bubble = document.querySelector(COUNT_BUBBLE);
    if (bubble) {
      var visible = bubble.querySelector('[aria-hidden="true"]') ||
                    bubble.querySelector('span');
      if (visible) visible.textContent = count;

      var sr = bubble.querySelector('.visually-hidden');
      if (sr) sr.textContent = count + ' item' + (count === 1 ? '' : 's');

      bubble.classList.toggle('hidden', count === 0);
      bubble.classList.toggle('visually-hidden', count === 0);
    }

    // Fallback for text-style icons such as "Cart(5)".
    var link = document.querySelector(COUNT_LINK);
    if (link && !bubble) {
      var span = link.querySelector('span:not(.visually-hidden)');
      if (span && /\(\s*\d+\s*\)/.test(span.textContent)) {
        span.textContent = span.textContent.replace(/\(\s*\d+\s*\)/, '(' + count + ')');
      }
    }
  }

  function renderSections(res) {
    if (!res || !res.sections) return;
    Object.keys(res.sections).forEach(function (id) {
      var html = res.sections[id];
      if (!html) return;
      var sel = SELECTORS[id] || '#' + id;
      var target = document.querySelector(sel) || document.getElementById('shopify-section-' + id);
      if (!target) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var source = doc.querySelector(sel) ||
                   doc.getElementById('shopify-section-' + id) ||
                   doc.querySelector('.shopify-section');
      if (source) target.innerHTML = source.innerHTML;
    });

    updateCartCount(res.item_count);

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
  }

  // --- reconcile (slow path) -------------------------------------------------

  function sync() {
    if (busy) { queued = true; return Promise.resolve(); }
    busy = true;

    return json('/cart.js')
      .then(function (cart) {
        var hasSub = cart.items.some(isSubscription);
        var giftLines = cart.items.filter(isGiftLine);

        var present = {};
        giftLines.forEach(function (l) { present[l.variant_id] = true; });

        // A gift that was in the cart last time and isn't now was removed by
        // the customer. Remember that so it isn't added back.
        if (lastSeen) {
          GIFT_VARIANT_IDS.forEach(function (id) {
            if (lastSeen[id] && !present[id]) decline(id);
          });
        }

        // No qualifying subscription left: strip every gift and forget the
        // declines, so a fresh subscription offers the full set again.
        if (!hasSub || !ELIGIBLE) {
          allGiftsPresent = false;
          lastSeen = null;
          clearDeclined();

          if (!giftLines.length) {
            updateCartCount(cart.item_count);
            return;
          }
          var clear = {};
          giftLines.forEach(function (l) { clear[l.key] = 0; });
          return updateLines(clear).then(renderSections);
        }

        // Eligible: at most one line of quantity 1 per gift.
        var seen = {};
        var fixes = {};
        var needsFix = false;

        giftLines.forEach(function (l) {
          if (seen[l.variant_id]) {          // duplicate line for the same gift
            fixes[l.key] = 0;
            needsFix = true;
          } else {
            seen[l.variant_id] = true;
            if (l.quantity !== 1) {          // customer bumped the quantity
              fixes[l.key] = 1;
              needsFix = true;
            }
          }
        });

        // Only add gifts that are missing AND were never turned down.
        var missing = GIFT_VARIANT_IDS.filter(function (id) {
          return !seen[id] && !isDeclined(id);
        });

        allGiftsPresent = GIFT_VARIANT_IDS.every(function (id) {
          return seen[id] || isDeclined(id);
        });

        if (!needsFix && !missing.length) {
          lastSeen = seen;
          updateCartCount(cart.item_count);
          return;
        }

        var chain = needsFix ? updateLines(fixes) : Promise.resolve(null);
        return chain.then(function (res) {
          if (!missing.length) {
            lastSeen = seen;
            return renderSections(res);
          }
          missing.forEach(function (id) { seen[id] = true; });
          lastSeen = seen;
          allGiftsPresent = true;
          return addGifts(missing).then(function (addRes) {
            renderSections(addRes);
            // /cart/add.js gives no item_count, so read it back.
            return json('/cart.js').then(function (c) { updateCartCount(c.item_count); });
          });
        });
      })
      .catch(function (e) { console.warn('[free-gift]', e); })
      .then(function () {
        busy = false;
        if (queued) { queued = false; return sync(); }
      });
  }

  // --- request body parsing (fast path) --------------------------------------

  function readBody(init) {
    var b = init && init.body;
    if (!b) return null;

    if (typeof b === 'string') {
      try {
        var parsed = JSON.parse(b);
        return parsed && parsed.items ? parsed : null;
      } catch (e) { return null; }
    }

    if (typeof FormData !== 'undefined' && b instanceof FormData) {
      var flat = {};
      b.forEach(function (v, k) {
        if (flat[k] === undefined) flat[k] = v;
        else if (Array.isArray(flat[k])) flat[k].push(v);
        else flat[k] = [flat[k], v];
      });

      if (flat['items[0][id]'] !== undefined || flat.id === undefined) return null;

      var item = { id: flat.id, quantity: Number(flat.quantity || 1) };
      if (flat.selling_plan) item.selling_plan = flat.selling_plan;

      var props = {};
      Object.keys(flat).forEach(function (k) {
        var m = k.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = flat[k];
      });
      if (Object.keys(props).length) item.properties = props;

      var payload = { items: [item] };
      var sections = flat.sections;
      if (sections) payload.sections = typeof sections === 'string' ? sections.split(',') : sections;
      if (flat.sections_url) payload.sections_url = flat.sections_url;
      return payload;
    }

    return null;
  }

  // True only if the request adds a subscription to a non-excluded product.
  function wantsSubscription(payload) {
    return payload.items.some(function (i) {
      return !!i.selling_plan && !isExcluded(i.id);
    });
  }

  function giftsNotInPayload(payload) {
    var inPayload = payload.items.map(function (i) { return Number(i.id); });
    return GIFT_VARIANT_IDS.filter(function (id) {
      return inPayload.indexOf(id) === -1 && !isDeclined(id);
    });
  }

  // --- interceptors ----------------------------------------------------------

  var ADD = /\/cart\/add(\.js)?/;
  var MUTATIONS = /\/cart\/(add|change|update|clear)(\.js)?/;

  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';

    // Fast path: piggyback the gifts onto the theme's own add request.
    if (ADD.test(url) && ELIGIBLE && !allGiftsPresent && !busy) {
      var payload = readBody(init);
      if (payload && wantsSubscription(payload)) {
        var toAdd = giftsNotInPayload(payload);
        if (toAdd.length) {
          toAdd.forEach(function (id) { payload.items.push(giftItem(id)); });
          if (!payload.sections) {
            payload.sections = SECTIONS;
            payload.sections_url = window.location.pathname;
          }
          allGiftsPresent = true;

          var newInit = {};
          for (var k in init) if (init.hasOwnProperty(k)) newInit[k] = init[k];
          newInit.method = 'POST';
          newInit.body = JSON.stringify(payload);
          newInit.headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

          return nativeFetch.call(this, url, newInit).then(function (res) {
            json('/cart.js').then(function (c) {
              updateCartCount(c.item_count);
              // Seed lastSeen so the next reconcile can spot a removal.
              var s = {};
              c.items.filter(isGiftLine).forEach(function (l) { s[l.variant_id] = true; });
              lastSeen = s;
            });
            return res;
          }, function (err) {
            allGiftsPresent = false;   // request failed, cache is wrong
            throw err;
          });
        }
      }
    }

    // Slow path: reconcile after any other cart mutation.
    var result = nativeFetch.apply(this, arguments);
    if (MUTATIONS.test(url) && !busy) {
      result.then(function () { setTimeout(sync, 0); }, function () {});
    }
    return result;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (MUTATIONS.test(String(url))) {
      this.addEventListener('load', function () { if (!busy) setTimeout(sync, 0); });
    }
    return nativeOpen.apply(this, arguments);
  };

  ['cart:updated', 'cart:change'].forEach(function (evt) {
    document.addEventListener(evt, function () { if (!busy) sync(); });
  });

  // Prime the cache once on load, without blocking.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  window.freeGiftSync = sync;          // manual trigger for debugging
  window.freeGiftReset = function () {  // re-offer every gift
    clearDeclined();
    lastSeen = null;
    allGiftsPresent = false;
    return sync();
  };
})();

/* ---------------------------------------------------------------------------
Liquid, in layout/theme.liquid before </body>.

One-time setup: in Shopify admin create an AUTOMATED collection with the handle
`no-free-gift` and the condition "Product tag is equal to no-free-gift". It can
stay unpublished from sales channels; Liquid can still read it. After that,
tagging a product `no-free-gift` is all that's needed to exclude it.

{%- assign is_subscriber = false -%}
{%- if customer and customer.tags contains 'Active Subscriber' -%}
  {%- assign is_subscriber = true -%}
{%- endif -%}
<script>
  window.freeGiftConfig = {
    eligible: {% if is_subscriber %}false{% else %}true{% endif %},
    excludedVariantIds: [
      {%- for p in collections['no-free-gift'].products -%}
        {%- for v in p.variants -%}{{ v.id }},{%- endfor -%}
      {%- endfor -%}0
    ]
  };
</script>
<script src="{{ 'free-gift-subscription.js' | asset_url }}" defer></script>

Note: collection.products returns at most 50 products without pagination.

To label the lines in the cart template:
  {% if item.properties._free_gift %} ... render as "Free gift" ... {% endif %}
--------------------------------------------------------------------------- */
